import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Gift, Trophy, History as HistoryIcon, FerrisWheel, Copy, Check, Lock, RefreshCw, X, Sparkles,
} from 'lucide-react';

// ─── Spin & Win ──────────────────────────────────────────────────────────────
// The wheel does NOT decide anything. It asks the server for a spin, and the
// server answers with the slice index to stop on - so the animation is theatre
// played over a result that already exists and has already been paid out.
//
// Everything the wheel draws (labels, emoji, slice order) comes from /spin/state
// too: the backend owns the prize list, and editing it there re-renders both
// apps without a release.

const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000') + '/api/v1';

type PrizeKind = 'none' | 'points' | 'cash' | 'ride_discount' | 'free_ride' | 'commission_relief';

interface Slice { id: string; label: string; emoji: string; kind: PrizeKind; value: number }
interface Reward { id: string; kind: string; label: string; value: number; code: string | null; expires_at: string | null }
interface Leader { rank: number; userId: string; name: string; points: number }
interface HistoryRow { id: string; prize_label: string; prize_kind: PrizeKind; is_win: boolean; points_awarded: number; created_at: string }

interface State {
  role: string;
  enabled: boolean;
  lockedMessage: string | null;
  wheel: Slice[];
  freeRideCap: number;
  spinsAvailable: number;
  spinsUsed: number;
  points: number;
  dryStreak: number;
  rewards: Reward[];
  leaders: Leader[];
  history: HistoryRow[];
}

interface SpinResult {
  index: number;
  prize: Slice;
  isWin: boolean;
  message: string;
  code: string | null;
  pointsAwarded: number;
  spinsAvailable: number;
  points: number;
}

// How long the wheel turns for. Long enough to feel like a draw, short enough
// that a user with five banked spins isn't held hostage by the animation.
const SPIN_MS = 4200;
const FULL_TURNS = 5;

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('accessToken')}` });

/** A point on the wheel rim, for an angle measured clockwise from 12 o'clock. */
function rim(cx: number, cy: number, r: number, deg: number) {
  const rad = ((deg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

const SpinAndWin: React.FC = () => {
  const [state, setState] = useState<State | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'spin' | 'rewards' | 'leaderboard' | 'history'>('spin');

  const [spinning, setSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [result, setResult] = useState<SpinResult | null>(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState<string | null>(null);

  // Kept in a ref as well as state: the next spin has to continue from where the
  // last one stopped, and reading it out of the closure would replay the old value.
  const rotationRef = useRef(0);
  // Guards against the screen unmounting mid-spin (back button, ride request
  // arriving) and a timer then writing into a dead component.
  const aliveRef = useRef(true);
  useEffect(() => () => { aliveRef.current = false; }, []);

  const load = useCallback(async (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    try {
      const res = await fetch(`${API_URL}/spin/state`, { headers: authHeaders() });
      if (res.ok) setState(await res.json());
    } catch { /* offline - keep whatever is on screen */ }
    if (aliveRef.current) setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Memoised, not `state?.wheel || []`: that fallback allocates a fresh array on
  // every render, which makes the slice geometry below recompute every time and
  // trips react-hooks/exhaustive-deps (a build error under CI).
  const wheel = useMemo(() => state?.wheel || [], [state?.wheel]);
  const sliceCount = wheel.length || 8;
  const sliceDeg = 360 / sliceCount;

  const doSpin = async () => {
    if (spinning || !state?.enabled || (state?.spinsAvailable || 0) <= 0) return;
    setError('');
    setResult(null);
    setSpinning(true);

    let data: SpinResult;
    try {
      const res = await fetch(`${API_URL}/spin`, { method: 'POST', headers: authHeaders() });
      const body = await res.json();
      if (!res.ok) {
        setSpinning(false);
        setError(body.error || 'Could not spin right now');
        // A lock or an exhausted balance both mean the screen is stale.
        load(false);
        return;
      }
      data = body;
    } catch {
      setSpinning(false);
      setError('No connection - check your network and try again');
      return;
    }

    // Land the pointer (fixed at 12 o'clock) on the centre of the winning slice,
    // always turning forwards so the wheel never visibly snaps backwards.
    const centre = data.index * sliceDeg + sliceDeg / 2;
    const want = ((360 - centre) % 360 + 360) % 360;
    const current = ((rotationRef.current % 360) + 360) % 360;
    const next = rotationRef.current + ((want - current + 360) % 360) + 360 * FULL_TURNS;
    rotationRef.current = next;
    setRotation(next);

    window.setTimeout(() => {
      if (!aliveRef.current) return;
      setSpinning(false);
      setResult(data);
      setState((s) => (s ? { ...s, spinsAvailable: data.spinsAvailable, points: data.points } : s));
      // Refresh in the background so Rewards/History/Leaderboard catch up
      // without the result card flickering away underneath the user.
      load(false);
    }, SPIN_MS);
  };

  const copy = (code: string) => {
    navigator.clipboard?.writeText(code);
    setCopied(code);
    setTimeout(() => { if (aliveRef.current) setCopied(null); }, 2000);
  };

  const canSpin = !!state?.enabled && (state?.spinsAvailable || 0) > 0 && !spinning;

  // ─── Wheel geometry ────────────────────────────────────────────────────────
  const SIZE = 300;
  const C = SIZE / 2;
  const R = C - 8;

  const slices = useMemo(() => wheel.map((prize, i) => {
    const a1 = i * sliceDeg;
    const a2 = (i + 1) * sliceDeg;
    const p1 = rim(C, C, R, a1);
    const p2 = rim(C, C, R, a2);
    const label = rim(C, C, R * 0.64, a1 + sliceDeg / 2);
    const icon = rim(C, C, R * 0.82, a1 + sliceDeg / 2);
    return {
      prize,
      // largeArc is 0 for any slice under 180° - true for every wheel we ship,
      // but computed rather than hardcoded so a 5-slice wheel still draws.
      d: `M ${C} ${C} L ${p1.x} ${p1.y} A ${R} ${R} 0 ${sliceDeg > 180 ? 1 : 0} 1 ${p2.x} ${p2.y} Z`,
      fill: i % 2 === 0 ? '#15161b' : '#1f2027',
      label, icon,
      // The prizes worth having read in gold; the rest stay quiet.
      gold: prize.kind === 'cash' || prize.kind === 'free_ride' || prize.value >= 20,
    };
  }), [wheel, sliceDeg, C, R]);

  if (loading && !state) {
    return (
      <div className="flex justify-center py-24">
        <div className="w-8 h-8 border-2 border-safaricab-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="pb-28">
      {/* ── Locked ──────────────────────────────────────────────────────────
          The admin can pause the wheel from the dashboard. Say so plainly
          rather than showing a dead SPIN button that does nothing. */}
      {state && !state.enabled && (
        <div className="mx-4 mt-4 bg-safaricab-card border border-white/10 rounded-2xl p-5 text-center">
          <Lock className="w-8 h-8 text-white/40 mx-auto mb-3" />
          <p className="text-white font-bold mb-1">Spin &amp; Win is paused</p>
          <p className="text-white/50 text-sm">{state.lockedMessage}</p>
        </div>
      )}

      {tab === 'spin' && (
        <div className="px-4 pt-4">
          {/* ── The wheel ── */}
          <div className={`relative mx-auto ${!state?.enabled ? 'opacity-40 pointer-events-none' : ''}`} style={{ width: SIZE, maxWidth: '100%' }}>
            {/* Pointer, fixed at 12 o'clock - the wheel turns under it. */}
            <div className="absolute left-1/2 -translate-x-1/2 -top-1 z-10"
                 style={{ width: 0, height: 0, borderLeft: '11px solid transparent', borderRight: '11px solid transparent', borderTop: '20px solid #F5A623' }} />

            <motion.svg
              viewBox={`0 0 ${SIZE} ${SIZE}`}
              className="w-full h-auto drop-shadow-2xl"
              animate={{ rotate: rotation }}
              transition={{ duration: SPIN_MS / 1000, ease: [0.13, 0.9, 0.12, 1] }}
              style={{ transformOrigin: '50% 50%' }}
            >
              <defs>
                <linearGradient id="spinRim" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#FFD700" />
                  <stop offset="50%" stopColor="#F5A623" />
                  <stop offset="100%" stopColor="#9a6410" />
                </linearGradient>
              </defs>

              <circle cx={C} cy={C} r={R + 5} fill="url(#spinRim)" />
              {slices.map((s, i) => (
                <g key={s.prize.id + i}>
                  <path d={s.d} fill={s.fill} stroke="rgba(245,166,35,0.35)" strokeWidth={1} />
                  <text x={s.icon.x} y={s.icon.y} textAnchor="middle" dominantBaseline="middle" fontSize={15}>
                    {s.prize.emoji}
                  </text>
                  <text
                    x={s.label.x} y={s.label.y}
                    textAnchor="middle" dominantBaseline="middle"
                    fontSize={sliceCount > 8 ? 8.5 : 10}
                    fontWeight="700"
                    fill={s.gold ? '#F5A623' : 'rgba(255,255,255,0.82)'}
                  >
                    {s.prize.label}
                  </text>
                </g>
              ))}
              <circle cx={C} cy={C} r={R * 0.03} fill="none" />
            </motion.svg>

            {/* Hub. Its own button rather than an onClick on the svg, so it
                doesn't rotate away from the finger mid-spin. */}
            <button
              onClick={doSpin}
              disabled={!canSpin}
              aria-label="Spin the wheel"
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[88px] h-[88px] rounded-full font-extrabold text-sm tracking-wide
                         border-4 border-safaricab-gold/80 shadow-[0_0_28px_rgba(245,166,35,0.35)] transition-transform active:scale-95
                         disabled:opacity-60 disabled:active:scale-100
                         bg-gradient-to-b from-safaricab-gold to-[#c47f14] text-black"
            >
              {spinning ? '…' : 'SPIN'}
            </button>
          </div>

          {/* ── Spin balance ── */}
          <div className="mt-6 text-center">
            {(state?.spinsAvailable || 0) > 0 ? (
              <p className="text-safaricab-gold font-bold">
                {state?.spinsAvailable === 1
                  ? 'You have 1 free spin — it’s on the house 🎉'
                  : `You have ${state?.spinsAvailable} free spins 🎉`}
              </p>
            ) : (
              <p className="text-white/50 text-sm">🚗 Complete your next SafariCab ride to unlock a FREE SPIN!</p>
            )}
            {/* The pity mechanic, said out loud. Hiding it would waste the one
                thing that actually brings a regular back to the wheel. */}
            {(state?.dryStreak || 0) >= 4 && state?.enabled && (
              <p className="text-white/40 text-xs mt-1 flex items-center justify-center gap-1">
                <Sparkles className="w-3 h-3 text-safaricab-gold" />
                {state!.dryStreak} spins without a prize — your odds are boosted
              </p>
            )}
            <p className="text-white/30 text-xs mt-2">{state?.points || 0} points this season</p>
          </div>

          {error && <p className="text-red-400 text-sm text-center mt-3">{error}</p>}
        </div>
      )}

      {/* ── Rewards ── */}
      {tab === 'rewards' && (
        <div className="px-4 pt-4">
          {!state?.rewards?.length ? (
            <div className="text-center py-16">
              <Gift className="w-12 h-12 text-white/15 mx-auto mb-3" />
              <p className="text-white/40 text-sm">No active rewards yet.</p>
              <p className="text-white/25 text-xs mt-1">Spin the wheel to win discounts and free rides.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {state.rewards.map((r) => (
                <div key={r.id} className="bg-safaricab-card border border-safaricab-gold/25 rounded-2xl p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-safaricab-gold/15 flex items-center justify-center flex-shrink-0">
                      <Gift className="w-5 h-5 text-safaricab-gold" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-semibold text-sm">{r.label}</p>
                      {r.expires_at && (
                        <p className="text-white/40 text-xs mt-0.5">
                          Expires {new Date(r.expires_at).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                  {r.code && (
                    <>
                      <div className="flex items-center gap-2 mt-3">
                        <div className="flex-1 bg-black/40 border border-white/10 rounded-xl py-2.5 px-3 text-safaricab-gold font-mono font-bold tracking-widest text-center text-sm">
                          {r.code}
                        </div>
                        <button onClick={() => copy(r.code!)} aria-label="Copy code"
                                className="bg-black/40 border border-white/10 rounded-xl p-2.5 text-safaricab-gold">
                          {copied === r.code ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        </button>
                      </div>
                      <p className="text-white/35 text-[11px] mt-2">
                        Enter this code at checkout when you book. It only works on your account.
                      </p>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Leaderboard ── */}
      {tab === 'leaderboard' && (
        <div className="px-4 pt-4">
          <p className="text-white/40 text-xs mb-3">Points earned from spins over the last 7 days.</p>
          {!state?.leaders?.length ? (
            <div className="text-center py-16">
              <Trophy className="w-12 h-12 text-white/15 mx-auto mb-3" />
              <p className="text-white/40 text-sm">No spins yet this week — be the first.</p>
            </div>
          ) : (
            <div className="bg-safaricab-card border border-white/10 rounded-2xl overflow-hidden">
              {state.leaders.map((l) => (
                <div key={l.userId} className="flex items-center gap-3 px-4 py-3 border-b border-white/5 last:border-0">
                  <span className="w-6 text-center text-sm">
                    {l.rank === 1 ? '🥇' : l.rank === 2 ? '🥈' : l.rank === 3 ? '🥉' : <span className="text-white/30">{l.rank}</span>}
                  </span>
                  <div className="w-8 h-8 rounded-full bg-safaricab-gold/20 flex items-center justify-center text-safaricab-gold text-xs font-bold flex-shrink-0">
                    {l.name.charAt(0).toUpperCase()}
                  </div>
                  <p className="text-white text-sm flex-1 truncate">{l.name}</p>
                  <p className="text-safaricab-gold font-bold text-sm">{l.points}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── History ── */}
      {tab === 'history' && (
        <div className="px-4 pt-4">
          {!state?.history?.length ? (
            <div className="text-center py-16">
              <HistoryIcon className="w-12 h-12 text-white/15 mx-auto mb-3" />
              <p className="text-white/40 text-sm">You haven&apos;t spun yet.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {state.history.map((h) => (
                <div key={h.id} className="flex items-center gap-3 bg-safaricab-card border border-white/10 rounded-xl px-3 py-3">
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${h.is_win ? 'bg-safaricab-gold' : 'bg-white/20'}`} />
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${h.is_win ? 'text-safaricab-gold' : 'text-white/70'}`}>{h.prize_label}</p>
                    <p className="text-white/30 text-[11px]">{new Date(h.created_at).toLocaleString()}</p>
                  </div>
                  {h.points_awarded > 0 && <span className="text-white/40 text-xs">+{h.points_awarded} pts</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Tab strip ──────────────────────────────────────────────────────
          Sits above the safe-area inset, not on it - on a gesture-nav Android
          the bottom bar would otherwise eat the taps. */}
      <div className="fixed left-0 right-0 bottom-0 bg-safaricab-dark border-t border-white/10 flex z-20"
           style={{ paddingBottom: 'max(var(--sai-bottom), 8px)' }}>
        {([
          { id: 'spin', label: 'Spin', icon: FerrisWheel },
          { id: 'rewards', label: 'Rewards', icon: Gift, badge: state?.rewards?.length || 0 },
          { id: 'leaderboard', label: 'Leaders', icon: Trophy },
          { id: 'history', label: 'History', icon: HistoryIcon },
        ] as const).map(({ id, label, icon: Icon, ...rest }) => (
          <button key={id} onClick={() => setTab(id)}
                  className={`flex-1 flex flex-col items-center gap-1 py-2.5 relative ${tab === id ? 'text-safaricab-gold' : 'text-white/40'}`}>
            <Icon className="w-5 h-5" />
            <span className="text-[10px] font-medium">{label}</span>
            {'badge' in rest && (rest as any).badge > 0 && (
              <span className="absolute top-1.5 right-[28%] min-w-[15px] h-[15px] px-1 rounded-full bg-safaricab-gold text-black text-[9px] font-bold flex items-center justify-center">
                {(rest as any).badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── Result ── */}
      <AnimatePresence>
        {result && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setResult(null)}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[60] flex items-center justify-center px-6"
          >
            <motion.div
              initial={{ scale: 0.85, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: 'spring', damping: 18, stiffness: 260 }}
              onClick={(e) => e.stopPropagation()}
              className={`w-full max-w-xs rounded-3xl p-6 text-center border ${
                result.isWin ? 'bg-safaricab-card border-safaricab-gold/50' : 'bg-safaricab-card border-white/10'}`}
            >
              <button onClick={() => setResult(null)} aria-label="Close"
                      className="absolute right-4 top-4 text-white/30 p-1"><X className="w-4 h-4" /></button>
              <div className="text-5xl mb-3">{result.prize.emoji}</div>
              <p className={`font-extrabold text-2xl mb-1 ${result.isWin ? 'text-safaricab-gold' : 'text-white'}`}>
                {result.prize.label}
              </p>
              <p className="text-white/60 text-sm">{result.message}</p>

              {result.code && (
                <div className="flex items-center gap-2 mt-4">
                  <div className="flex-1 bg-black/40 border border-white/10 rounded-xl py-2.5 px-3 text-safaricab-gold font-mono font-bold tracking-widest text-sm">
                    {result.code}
                  </div>
                  <button onClick={() => copy(result.code!)} aria-label="Copy code"
                          className="bg-black/40 border border-white/10 rounded-xl p-2.5 text-safaricab-gold">
                    {copied === result.code ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              )}

              {result.pointsAwarded > 0 && (
                <p className="text-white/35 text-xs mt-3">+{result.pointsAwarded} leaderboard points</p>
              )}

              <button
                onClick={() => { setResult(null); if ((state?.spinsAvailable || 0) > 0) setTab('spin'); }}
                className="w-full mt-5 bg-safaricab-gold text-black font-bold py-3 rounded-xl"
              >
                {(state?.spinsAvailable || 0) > 0 ? 'Spin again' : 'Done'}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Manual refresh, tucked away - the screen self-refreshes after a spin. */}
      {tab !== 'spin' && (
        <button onClick={() => load()} aria-label="Refresh"
                className="fixed right-4 bottom-24 w-10 h-10 rounded-full bg-safaricab-card border border-white/10 text-white/40 flex items-center justify-center z-20">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      )}
    </motion.div>
  );
};

export default SpinAndWin;
